/**
 * features/finance/Payments.tsx
 * ------------------------------------------------------------------
 * P3 — Capture payments + issue receipts, now with an at-a-glance
 * view: invoice-category sub-tabs (All / Unpaid / Part-paid / Paid /
 * Overdue) with live counts and outstanding totals. Browse a category,
 * pick an invoice, record a payment (EFT/card per LKC policy), and the
 * invoice's paid/balance/status update immediately via applyPayment.
 * Every capture mints a receipt number, is audited, and prints an A4
 * receipt PDF in the payslip house style.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { HandCoins, Search, X, FileDown, CheckCircle2, Clock, ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react';
import { jsPDF } from 'jspdf';
import { AccentButton, GradientHero, SoftCard, glassFieldClass } from '@/features/finance/ui';
import { NumberField } from '@/components/ui/glass';
import { Spinner } from '@/components/ui/primitives';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import { useAuth } from '@/contexts/AuthContext';
import { useFinanceData } from '@/features/finance/useFinanceData';
import ReversePaymentPanel from '@/features/finance/ReversePaymentPanel';
import { finance } from '@/features/finance/financeWrite';
import { recordFinanceAudit } from '@/lib/financeAudit';
import { SCHOOL, termLabel } from '@/lib/school.config';
import { loadLogoDataUrl } from '@/lib/loadLogo';
import {
  applyPayment, formatReceiptNo, fmtPula, fmtBalance, round2, PAYMENT_METHOD_LABELS,
  instalmentOverdue, nextInstalmentDue,
  type Invoice, type Payment, type PaymentMethod,
  accountStandings, isOpeningBalance, OPENING_BALANCE_LABEL,
  invoiceNoCommonPart, invoiceNoTailMatch,
} from '@/lib/financeCore';

const METHODS: PaymentMethod[] = ['eft', 'card', 'cash', 'mobile', 'other'];

/** How many invoices to show before the "View all" toggle — keeps the
 *  Recent payments card reachable without a long scroll. */
const INVOICE_PREVIEW = 8;

const STATUS_BADGE: Record<string, string> = {
  issued: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  part_paid: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  paid: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  void: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
};
const STATUS_LABEL: Record<string, string> = { issued: 'Unpaid', part_paid: 'Part-paid', paid: 'Paid', void: 'Void' };

type Cat = 'all' | 'unpaid' | 'part_paid' | 'paid' | 'overdue';
const CATS: { key: Cat; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'unpaid', label: 'Unpaid' },
  { key: 'part_paid', label: 'Part-paid' },
  { key: 'paid', label: 'Paid' },
  { key: 'overdue', label: 'Overdue' },
];

function matchCat(i: Invoice, cat: Cat, asOf: Date): boolean {
  if (i.status === 'void') return false;
  switch (cat) {
    case 'all': return true;
    case 'unpaid': return i.status === 'issued';
    case 'part_paid': return i.status === 'part_paid';
    case 'paid': return i.status === 'paid';
    case 'overdue': return instalmentOverdue(i, asOf) > 0;
  }
}

interface ReceiptData {
  receiptNo: string; dateStr: string; studentName: string; invoiceNo: string;
  term: string; year: string; method: PaymentMethod; reference?: string;
  amount: number; invoiceTotal: number; totalPaid: number; balance: number; note?: string;
}

export default function Payments() {
  const data = useFinanceData();
  const { userData, permissions } = useAuth();
  const actor = { id: userData?.uid ?? 'unknown', email: userData?.email, name: userData?.displayName };

  const [cat, setCat] = useState<Cat>('overdue');
  const [showAllInvoices, setShowAllInvoices] = useState(false);
  const [query, setQuery] = useState('');
  /* Search by the pupil's name, or by the invoice number a parent quoted.
     In invoice mode the common part ("INV/2026/") is fixed in front of the
     box and only the unique digits are typed. */
  const [searchBy, setSearchBy] = useState<'name' | 'invoice'>('name');
  const invYears = useMemo(() => {
    const ys = new Set(data.invoices.map((i) => i.year).filter(Boolean));
    ys.add(String(new Date().getFullYear()));
    return [...ys].sort().reverse();
  }, [data.invoices]);
  const [invYear, setInvYear] = useState(String(new Date().getFullYear()));
  const commonPart = invoiceNoCommonPart(data.settings, invYear);
  const [selectedId, setSelectedId] = useState('');
  const [amount, setAmount] = useState(0);
  const [method, setMethod] = useState<PaymentMethod>('eft');
  const [reference, setReference] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [lastReceipt, setLastReceipt] = useState<ReceiptData | null>(null);
  const [sc, setSc] = useState<{ show: boolean; status: 'saving' | 'success' | 'error'; message: string }>({ show: false, status: 'saving', message: '' });

  const invoice = data.invoices.find((i) => i.id === selectedId);
  const nowMs = Date.now();
  const asOf = new Date(nowMs);

  useEffect(() => {
    if (invoice) { setAmount(Math.max(0, invoice.balance)); setLastReceipt(null); }
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Collapse back to the preview whenever the category or search changes.
  useEffect(() => { setShowAllInvoices(false); }, [cat, query, searchBy, invYear]);

  const counts = useMemo(() => {
    const c: Record<Cat, { n: number; due: number }> = {
      all: { n: 0, due: 0 }, unpaid: { n: 0, due: 0 }, part_paid: { n: 0, due: 0 }, paid: { n: 0, due: 0 }, overdue: { n: 0, due: 0 },
    };
    for (const i of data.invoices) {
      for (const k of ['all', 'unpaid', 'part_paid', 'paid', 'overdue'] as Cat[]) {
        if (matchCat(i, k, asOf)) {
          c[k].n += 1;
          const add = k === 'overdue' ? instalmentOverdue(i, asOf) : Math.max(0, i.balance);
          c[k].due = round2(c[k].due + add);
        }
      }
    }
    return c;
  }, [data.invoices, nowMs]); // eslint-disable-line react-hooks/exhaustive-deps

  /* WHAT EACH PUPIL ACTUALLY OWES, across every document on their account.
     This screen lists INVOICES, and an invoice does not know that the same
     pupil is holding a credit on another one. Moswetsi Tyler Kgosi shows a
     P24,000 invoice marked Unpaid directly above a P20,000 credit, and the
     bursar is left to do the subtraction — which is exactly the confusion
     the account-standing work was written to end. */
  const standings = useMemo(() => accountStandings(data.invoices), [data.invoices]);

  /* The receivable net of credits. The tab figures above sum
     Math.max(0, balance) per invoice, so they count what is owed and ignore
     what is held — P16.2m against a true P12.1m. The tab totals stay as they
     are (each tab means what it says), and the honest figure sits beside
     them rather than replacing one with the other. */
  const netOutstanding = useMemo(() => {
    let n = 0;
    standings.forEach((st) => { if (st.balance > 0) n += st.balance; });
    return round2(n);
  }, [standings]);
  const heldInCredit = useMemo(() => {
    let n = 0;
    standings.forEach((st) => { n += st.inCredit; });
    return round2(n);
  }, [standings]);

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (searchBy === 'invoice' && q) {
      // A quoted invoice number is a lookup, not a browse: search every
      // tab, so an invoice that is not overdue is not hidden by the tab.
      return data.invoices
        .map((i) => ({ i, m: invoiceNoTailMatch(i.invoiceNo, commonPart, query) }))
        .filter((x) => x.m > 0 && x.i.status !== 'void')
        .sort((a, b) => b.m - a.m || a.i.invoiceNo.localeCompare(b.i.invoiceNo))
        .map((x) => x.i);
    }
    return data.invoices
      .filter((i) => matchCat(i, cat, asOf))
      .filter((i) => !q || (i.studentName ?? '').toLowerCase().includes(q) || i.invoiceNo.toLowerCase().includes(q))
      .sort((a, b) => Number(b.balance > 0) - Number(a.balance > 0) || (a.studentName ?? '').localeCompare(b.studentName ?? ''));
  }, [data.invoices, cat, query, searchBy, commonPart, nowMs]); // eslint-disable-line react-hooks/exhaustive-deps

  if (data.loading) {
    return (
      <div className="space-y-6">
        <GradientHero icon={HandCoins} title="Payments" subtitle="Capture payments and issue receipts" />
        <div className="flex justify-center py-16"><Spinner label="Loading payments…" /></div>
      </div>
    );
  }

  const amt = Number(amount) || 0;
  const after = invoice ? applyPayment(invoice, amt) : null;

  const record = async () => {
    if (!invoice || amt <= 0) return;
    setBusy(true);
    setSc({ show: true, status: 'saving', message: 'Recording payment…' });
    try {
      const seq = data.payments.length + 1;
      const receiptNo = formatReceiptNo(invoice.year, seq);
      const receivedAt = new Date(`${date}T12:00:00`).toISOString();
      const upd = applyPayment(invoice, amt);
      await finance.payments.add({
        invoiceId: invoice.id, studentId: invoice.studentId, studentName: invoice.studentName,
        familyId: invoice.familyId, amount: amt, method,
        reference: reference.trim() || undefined, receiptNo, receivedAt,
        recordedBy: actor.id, recordedByName: actor.name, note: note.trim() || undefined,
      });
      await finance.invoices.update(invoice.id, upd);
      await recordFinanceAudit({ action: 'payment.capture', actor, subjectId: invoice.id, after: { amount: amt, method, receiptNo } });

      setLastReceipt({
        receiptNo, dateStr: date, studentName: invoice.studentName ?? '', invoiceNo: invoice.invoiceNo,
        term: invoice.term, year: invoice.year, method, reference: reference.trim() || undefined,
        amount: amt, invoiceTotal: invoice.total, totalPaid: upd.paid, balance: upd.balance, note: note.trim() || undefined,
      });
      setSelectedId(''); setReference(''); setNote('');
      setSc({ show: true, status: 'success', message: `Payment of ${fmtPula(amt)} recorded — receipt ${receiptNo}.` });
    } catch (e) {
      console.error(e);
      setSc({ show: true, status: 'error', message: 'Could not record payment — please retry.' });
    } finally {
      setBusy(false);
    }
  };

  const downloadReceipt = async (r: ReceiptData) => {
    const logo = await loadLogoDataUrl();
    buildReceiptPdf(r, logo).save(`receipt_${r.receiptNo.replace(/\//g, '-')}.pdf`);
  };

  const recent = data.payments
    .filter((p) => !p.isReversal) // reversal entries are ledger offsets, not receipts
    .sort((a, b) => (b.receivedAt ?? '').localeCompare(a.receivedAt ?? ''))
    .slice(0, 12);

  return (
    <div className="space-y-6">
      <GradientHero icon={HandCoins} title="Payments" subtitle="Capture payments and issue receipts" />
      <SaveConfirmation show={sc.show} status={sc.status} message={sc.message} onDismiss={() => setSc((s) => ({ ...s, show: false }))} />

      {lastReceipt && !invoice && (
        <SoftCard hover={false}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle2 size={18} className="text-emerald-500" />
              <span className="text-gray-700 dark:text-gray-200">
                {fmtPula(lastReceipt.amount)} from <span className="font-medium">{lastReceipt.studentName}</span> · receipt {lastReceipt.receiptNo} · new balance {fmtBalance(lastReceipt.balance)}
              </span>
            </div>
            <AccentButton icon={FileDown} onClick={() => downloadReceipt(lastReceipt)}>Download receipt</AccentButton>
          </div>
        </SoftCard>
      )}

      {invoice ? (
        /* ---- Capture ---- */
        <SoftCard hover={false}>
          <button onClick={() => setSelectedId('')} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 mb-3">
            <ArrowLeft size={15} /> Back to invoices
          </button>
          <div className="flex items-center gap-2 mb-3">
            <span className="font-semibold text-gray-800 dark:text-gray-100">{invoice.studentName}</span>
            <span className="text-gray-400 text-sm">{invoice.invoiceNo} · {termLabel(invoice.term)} {invoice.year}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_BADGE[invoice.status]}`}>{STATUS_LABEL[invoice.status]}</span>
          </div>
          <div className="grid grid-cols-3 gap-3 text-sm mb-4">
            <div><span className="text-gray-400 block text-xs">Invoice total</span>{fmtPula(invoice.total)}</div>
            <div><span className="text-gray-400 block text-xs">Paid to date</span>{fmtPula(invoice.paid)}</div>
            <div><span className="text-gray-400 block text-xs">Balance</span><span className="font-semibold">{fmtBalance(invoice.balance)}</span></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Amount</span>
              <NumberField value={amount} onValue={setAmount} className={glassFieldClass('py-2')} />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Method</span>
              <select value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)} className={glassFieldClass('py-2')}>
                {METHODS.map((m) => <option key={m} value={m}>{PAYMENT_METHOD_LABELS[m]}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Reference</span>
              <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Bank ref / receipt slip" className={glassFieldClass('py-2')} />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Date received</span>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={glassFieldClass('py-2')} />
            </label>
          </div>
          <label className="block mt-3">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Note (optional)</span>
            <input value={note} onChange={(e) => setNote(e.target.value)} className={glassFieldClass('py-2')} />
          </label>
          {after && (
            <div className="flex items-center justify-between flex-wrap gap-3 pt-4">
              <div className="text-sm text-gray-600 dark:text-gray-300">
                After this payment: balance <span className="font-semibold text-gray-800 dark:text-gray-100">{fmtPula(after.balance)}</span>
                {after.balance < 0 && <span className="text-amber-600 dark:text-amber-300"> (credit of {fmtPula(-after.balance)})</span>}
                {amt > invoice.balance && invoice.balance >= 0 && <span className="text-amber-600 dark:text-amber-300"> — exceeds the outstanding balance</span>}
              </div>
              <AccentButton icon={HandCoins} onClick={record} disabled={busy || amt <= 0}>
                {busy ? 'Recording…' : 'Record payment'}
              </AccentButton>
            </div>
          )}
        </SoftCard>
      ) : (
        /* ---- Browse by category ---- */
        <SoftCard pad="p-0" hover={false}>
          <div className="px-4 pt-3 flex flex-wrap gap-1 border-b border-gray-100 dark:border-gray-700">
            {CATS.map((c) => {
              const activeTab = c.key === cat;
              return (
                <button
                  key={c.key}
                  onClick={() => setCat(c.key)}
                  className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${
                    activeTab
                      ? 'border-[var(--accent-strong,#0f766e)] text-[var(--accent-link,#0d9488)] dark:text-[var(--accent-link-dark,#5eead4)]'
                      : 'border-transparent text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium'
                  }`}
                >
                  {c.label}
                  <span className={`px-1.5 py-0.5 rounded-full text-xs ${activeTab ? 'bg-[var(--accent-link,#0d9488)]/15 text-[var(--accent-link,#0d9488)] dark:text-[var(--accent-link-dark,#5eead4)]' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>
                    {counts[c.key].n}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-[14rem] flex items-center gap-2 flex-wrap">
              <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden text-xs" role="group" aria-label="Search by">
                {([['name', 'Name'], ['invoice', 'Invoice no.']] as const).map(([k, label]) => (
                  <button key={k} type="button" aria-pressed={searchBy === k} onClick={() => { setSearchBy(k); setQuery(''); }}
                    className={`px-3 py-2 font-semibold border-r last:border-r-0 border-gray-200 dark:border-gray-600 ${searchBy === k
                      ? 'bg-[var(--accent-strong)] text-white' : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300'}`}>{label}</button>
                ))}
              </div>
              {searchBy === 'invoice' && invYears.length > 1 && (
                <select value={invYear} onChange={(e) => setInvYear(e.target.value)} aria-label="Invoice year" className={glassFieldClass('py-2 text-xs w-auto')}>
                  {invYears.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              )}
              <div className="relative flex-1 min-w-[12rem]">
                {searchBy === 'invoice' ? (
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm font-mono text-gray-500 dark:text-gray-400 pointer-events-none select-none">{commonPart}</span>
                ) : (
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                )}
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  inputMode={searchBy === 'invoice' ? 'numeric' : undefined}
                  placeholder={searchBy === 'invoice' ? '0808' : 'Filter by name or invoice #'}
                  aria-label={searchBy === 'invoice' ? `Invoice number after ${commonPart}` : 'Filter by name or invoice number'}
                  style={searchBy === 'invoice' ? { paddingLeft: `calc(0.75rem + ${commonPart.length}ch)` } : undefined}
                  className={glassFieldClass(`py-2 pr-8 text-sm ${searchBy === 'invoice' ? 'font-mono' : 'pl-8'}`)}
                />
                {query && <button onClick={() => setQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-700" aria-label="Clear"><X size={14} /></button>}
              </div>
              {searchBy === 'invoice' && query.trim() && (
                <span className="text-[11px] text-gray-500 dark:text-gray-400 w-full">
                  Searching every invoice, whatever tab is selected · {list.length} found
                </span>
              )}
            </div>
            {permissions.canViewFinanceTotals && (
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Outstanding in <span className="font-medium">{CATS.find((c) => c.key === cat)?.label}</span>: <span className="font-semibold text-gray-800 dark:text-gray-100">{fmtPula(counts[cat].due)}</span>
                {heldInCredit > 0 && (
                  <span className="block text-[11px] text-gray-400 mt-0.5">
                    {fmtPula(heldInCredit)} of that is covered by credit families already hold
                    {' \u00b7 '}net receivable {fmtPula(netOutstanding)}
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="px-4 pb-4 overflow-x-auto">
            {list.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-4">No invoices in this category.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                    <th className="py-2 pr-3 font-medium">Student</th>
                    <th className="py-2 px-3 font-medium">Invoice</th>
                    <th className="py-2 px-3 font-medium text-right">Total</th>
                    <th className="py-2 px-3 font-medium text-right">Balance</th>
                    <th className="py-2 px-3 font-medium">Status</th>
                    <th className="py-2 pl-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                  {(showAllInvoices ? list.slice(0, 80) : list.slice(0, INVOICE_PREVIEW)).map((i) => (
                    <tr key={i.id} className="hover:bg-[var(--accent-link,#0d9488)]/5">
                      <td className="py-2 pr-3 text-gray-800 dark:text-gray-100">{i.studentName ?? '—'}</td>
                      <td className="py-2 px-3 text-gray-500 dark:text-gray-400">
                        {i.invoiceNo}
                        {/* A POSITION CARRIED IN IS NOT AN INVOICE. "OPEN/2026/RB0247,
                            total -P20,000, Paid" tells the bursar nothing; it is the
                            Pastel balance brought over on 11 Aug. */}
                        {isOpeningBalance(i) && (
                          <div className="text-[11px] text-gray-400">
                            {i.balance < 0 ? 'credit brought forward' : 'balance brought forward'}
                            {' \u00b7 '}{OPENING_BALANCE_LABEL}
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-3 text-right text-gray-600 dark:text-gray-300">{fmtPula(i.total)}</td>
                      <td className="py-2 px-3 text-right font-medium text-gray-800 dark:text-gray-100">{fmtBalance(i.balance)}
                        {(() => {
                          /* THE ROW IS ONE DOCUMENT; THE FAMILY OWES ONE FIGURE.
                             Shown only when the two differ, so an ordinary invoice
                             on an ordinary account stays a single clean number. */
                          const st = standings.get(i.studentId);
                          if (!st || Math.abs(st.balance - i.balance) < 0.005) return null;
                          return (
                            <div className="text-[11px] font-normal text-teal-700 dark:text-teal-400">
                              account: {st.balance > 0.005 ? fmtPula(st.balance)
                                : st.balance < -0.005 ? `${fmtPula(st.inCredit)} in credit`
                                  : 'settled'}
                            </div>
                          );
                        })()}
                        {(() => {
                          const od = instalmentOverdue(i, asOf);
                          const nd = nextInstalmentDue(i, asOf);
                          if (od > 0) {
                            return (
                              <div className="text-[11px] font-normal text-rose-600 dark:text-rose-400">
                                {fmtPula(od)} overdue{nd ? ` · due ${nd.dueDate.slice(0, 10)}` : ''}
                              </div>
                            );
                          }
                          if (nd && i.balance > 0) {
                            return (
                              <div className="text-[11px] font-normal text-gray-400">
                                next {fmtPula(nd.balance)} · due {nd.dueDate.slice(0, 10)}
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </td>
                      <td className="py-2 px-3"><span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_BADGE[i.status]}`}>{STATUS_LABEL[i.status]}</span></td>
                      <td className="py-2 pl-3 text-right">
                        {i.balance > 0 ? (
                          <button onClick={() => setSelectedId(i.id)} className="text-xs font-medium text-[var(--accent-link,#0d9488)] hover:underline">Record payment</button>
                        ) : (
                          <button onClick={() => setSelectedId(i.id)} className="text-xs text-gray-400 hover:underline">View</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {list.length > INVOICE_PREVIEW && (
              <div className="mt-3 flex items-center justify-between gap-3">
                <span className="text-xs text-gray-400">
                  Showing {showAllInvoices ? Math.min(list.length, 80) : Math.min(list.length, INVOICE_PREVIEW)} of {list.length}
                </span>
                <button
                  onClick={() => setShowAllInvoices((v) => !v)}
                  className="inline-flex items-center gap-1 text-xs font-medium text-[var(--accent-link,#0d9488)] hover:underline"
                >
                  {showAllInvoices ? (<><ChevronUp size={13} /> Show less</>) : (<><ChevronDown size={13} /> View all {list.length}</>)}
                </button>
              </div>
            )}
            {showAllInvoices && list.length > 80 && <p className="text-xs text-gray-400 mt-2">Showing the first 80 — use the filter to narrow down.</p>}
          </div>
        </SoftCard>
      )}

      {/* Recent payments */}
      <SoftCard pad="p-0" hover={false}>
        <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
          <Clock size={15} className="text-gray-400" />
          <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">Recent payments</h3>
        </div>
        <div className="p-5">
          {recent.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No payments captured yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                    <th className="py-2 pr-3 font-medium">Date</th>
                    <th className="py-2 px-3 font-medium">Student</th>
                    <th className="py-2 px-3 font-medium">Method</th>
                    <th className="py-2 px-3 font-medium text-right">Amount</th>
                    <th className="py-2 pl-3 font-medium text-right">Receipt</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                  {recent.map((p) => (
                    <tr key={p.id}>
                      <td className="py-2 pr-3 text-gray-600 dark:text-gray-300">{(p.receivedAt ?? '').slice(0, 10)}</td>
                      <td className="py-2 px-3 text-gray-800 dark:text-gray-100">
                        {data.invoices.find((i) => i.id === p.invoiceId)?.studentName ?? p.studentName ?? '—'}
                        {p.reversed && <span className="ml-2 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300">Reversed</span>}
                      </td>
                      <td className={`py-2 px-3 text-gray-600 dark:text-gray-300 ${p.reversed ? 'line-through opacity-60' : ''}`}>{PAYMENT_METHOD_LABELS[p.method] ?? p.method}</td>
                      <td className="py-2 px-3 text-right font-medium text-gray-800 dark:text-gray-100">{fmtPula(p.amount)}</td>
                      <td className="py-2 pl-3 text-right">
                        <button onClick={() => reprint(p, data.invoices, downloadReceipt)} className="inline-flex items-center gap-1 text-xs text-[var(--accent-link,#0d9488)] hover:underline">
                          <FileDown size={13} /> {p.receiptNo ?? 'Receipt'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </SoftCard>

      {/* Reverse a payment — senior finance only */}
      {permissions.canManageFinanceConfig && <ReversePaymentPanel />}
    </div>
  );
}

function reprint(p: Payment, invoices: Invoice[], download: (r: ReceiptData) => void) {
  const inv = invoices.find((i) => i.id === p.invoiceId);
  download({
    receiptNo: p.receiptNo ?? 'RCP', dateStr: (p.receivedAt ?? '').slice(0, 10),
    studentName: p.studentName ?? inv?.studentName ?? '', invoiceNo: inv?.invoiceNo ?? '', term: inv?.term ?? '', year: inv?.year ?? '',
    method: p.method, reference: p.reference, amount: p.amount,
    invoiceTotal: inv?.total ?? 0, totalPaid: inv?.paid ?? p.amount, balance: inv?.balance ?? 0, note: p.note,
  });
}

/* ------------------------------------------------------------------
 * Receipt PDF (jsPDF) — A4, payslip house style.
 * ---------------------------------------------------------------- */
function buildReceiptPdf(r: ReceiptData, logo: string | null): jsPDF {
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const MX = 16; const right = 210 - MX;
  const NAVY: [number, number, number] = [27, 58, 107];
  const INK: [number, number, number] = [31, 41, 55];
  const MUTED: [number, number, number] = [107, 114, 128];
  const RULE: [number, number, number] = [209, 213, 219];
  const setT = (c: [number, number, number]) => pdf.setTextColor(c[0], c[1], c[2]);
  const setD = (c: [number, number, number]) => pdf.setDrawColor(c[0], c[1], c[2]);
  const setF = (c: [number, number, number]) => pdf.setFillColor(c[0], c[1], c[2]);
  let y = 16;

  if (logo) { try { pdf.addImage(logo, 'PNG', MX, y - 2, 18, 18); } catch { /* ignore */ } }
  setT(NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(14);
  pdf.text(SCHOOL.name, MX + 22, y + 4);
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8); setT(MUTED);
  pdf.text(`${SCHOOL.contact.phonePrimary}  ·  ${SCHOOL.contact.email}  ·  ${SCHOOL.contact.website}`, MX + 22, y + 9);
  y += 22;
  setD(RULE); pdf.setLineWidth(0.4); pdf.line(MX, y, right, y); y += 8;

  setT(NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(15);
  pdf.text('PAYMENT RECEIPT', MX, y);
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9); setT(MUTED);
  pdf.text(r.receiptNo, right, y, { align: 'right' });
  y += 6;
  pdf.setFontSize(8);
  pdf.text(`Date: ${r.dateStr}`, right, y, { align: 'right' });
  y += 8;

  const rowH = 9;
  const detail = (label: string, value: string, col: 0 | 1, rowIdx: number) => {
    const x = col === 0 ? MX : MX + 92;
    const yy = y + rowIdx * rowH;
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(7.5); setT(MUTED);
    pdf.text(label, x, yy);
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9.5); setT(INK);
    pdf.text(value || '—', x, yy + 4.5);
  };
  detail('RECEIVED FROM', r.studentName, 0, 0);
  detail('INVOICE', `${r.invoiceNo}${r.term ? `  (${r.term} ${r.year})` : ''}`, 1, 0);
  detail('METHOD', PAYMENT_METHOD_LABELS[r.method] ?? r.method, 0, 1);
  detail('REFERENCE', r.reference || '—', 1, 1);
  y += 2 * rowH + 4;

  setF([240, 244, 250]); setD(NAVY); pdf.setLineWidth(0.5);
  pdf.rect(MX, y, right - MX, 13, 'FD');
  setT(NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11);
  pdf.text('AMOUNT RECEIVED', MX + 3, y + 8.4);
  pdf.setFontSize(13);
  pdf.text(fmtPula(r.amount), right - 3, y + 8.6, { align: 'right' });
  y += 20;

  const line = (label: string, value: string, bold = false) => {
    setT(INK); pdf.setFont('helvetica', bold ? 'bold' : 'normal'); pdf.setFontSize(9);
    pdf.text(label, MX + 2, y); pdf.text(value, right - 2, y, { align: 'right' }); y += 6;
  };
  setD(RULE); pdf.setLineWidth(0.3); pdf.line(MX, y - 4, right, y - 4);
  line('Invoice total', fmtPula(r.invoiceTotal));
  line('Total paid to date', fmtPula(r.totalPaid));
  line('Balance', fmtPula(r.balance), true);
  y += 4;

  if (r.note) {
    setT(MUTED); pdf.setFont('helvetica', 'italic'); pdf.setFontSize(8);
    const lines = pdf.splitTextToSize(`Note: ${r.note}`, right - MX) as string[];
    pdf.text(lines, MX, y); y += lines.length * 4 + 2;
  }

  setT(MUTED); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8);
  pdf.text('Thank you. This receipt confirms the payment above against the stated invoice.', MX, y); y += 14;

  setD(INK); pdf.setLineWidth(0.3);
  const sigW = (right - MX - 10) / 2;
  pdf.line(MX, y, MX + sigW, y);
  pdf.line(MX + sigW + 10, y, right, y);
  pdf.setFontSize(8); setT(MUTED);
  pdf.text('Received by', MX, y + 4);
  pdf.text('School stamp', MX + sigW + 10, y + 4);

  return pdf;
}
